import { useRef, useState, type PointerEvent } from "react";
import clsx from "clsx";
import { GUILDS } from "../data/seed";
import { isRegionUnlocked } from "../lib/progression";
import type {
  Curriculum,
  GuildId,
  MapConnection,
  Region,
  StudentProgress,
} from "../types/domain";
import { Icon } from "./Icon";

type DragTarget =
  | { type: "region"; id: string }
  | { type: "connection"; id: string }
  | null;

function clampMapCoordinate(value: number) {
  return Math.max(3, Math.min(97, Number(value.toFixed(2))));
}

export function WorldMapCanvas({
  curriculum,
  progress,
  guild,
  selectedRegionId,
  onSelect,
  onMoveRegion,
  onMoveConnectionControl,
  selectedConnectionId,
  onSelectConnection,
  editing = false,
}: {
  curriculum: Curriculum;
  progress: StudentProgress;
  guild: GuildId;
  selectedRegionId?: string;
  onSelect(region: Region): void;
  onMoveRegion?(regionId: string, x: number, y: number): void;
  onMoveConnectionControl?(connectionId: string, x: number, y: number): void;
  selectedConnectionId?: string;
  onSelectConnection?(connection: MapConnection): void;
  editing?: boolean;
}) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const [dragTarget, setDragTarget] = useState<DragTarget>(null);
  const guildDetails = GUILDS.find((entry) => entry.id === guild)!;
  const connections = curriculum.connections.filter(
    (connection) => connection.guild === guild,
  );

  const getMapPoint = (event: PointerEvent) => {
    const rect = mapRef.current?.getBoundingClientRect();
    if (!rect) return null;
    return {
      x: clampMapCoordinate(((event.clientX - rect.left) / rect.width) * 100),
      y: clampMapCoordinate(((event.clientY - rect.top) / rect.height) * 100),
    };
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragTarget) return;
    const point = getMapPoint(event);
    if (!point) return;
    if (dragTarget.type === "region") {
      onMoveRegion?.(dragTarget.id, point.x, point.y);
    } else {
      onMoveConnectionControl?.(dragTarget.id, point.x, point.y);
    }
  };

  const stopDragging = () => setDragTarget(null);

  return (
    <div className="scrollbar-thin overflow-x-auto rounded-3xl">
      <div
        ref={mapRef}
        className={clsx(
          "map-terrain relative min-h-[560px] min-w-[920px] overflow-hidden rounded-3xl border border-slate-700/60",
          editing && "select-none",
        )}
        onPointerMove={handlePointerMove}
        onPointerUp={stopDragging}
        onPointerCancel={stopDragging}
        onPointerLeave={stopDragging}
      >
        <div className="absolute left-6 top-6 z-20 rounded-2xl border border-slate-700/70 bg-slate-950/80 p-3 backdrop-blur">
          <div className="flex items-center gap-3">
            <Icon name="Compass" size={28} style={{ color: guildDetails.color }} />
            <div>
              <p className="font-retro text-[8px] tracking-widest text-slate-400">
                ACTIVE ROUTE
              </p>
              <p className="mt-1 text-xs font-bold text-white">
                {editing ? "Faculty blueprint" : guildDetails.name}
              </p>
            </div>
          </div>
        </div>

        <svg
          className="absolute inset-0 z-0 h-full w-full"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {connections.map((connection) => {
            const from = curriculum.regions.find(
              (region) => region.id === connection.fromRegionId,
            );
            const to = curriculum.regions.find(
              (region) => region.id === connection.toRegionId,
            );
            if (!from || !to) return null;
            const selectedConnection = connection.id === selectedConnectionId;
            const path = `M ${from.x} ${from.y} Q ${connection.controlX} ${connection.controlY} ${to.x} ${to.y}`;
            return (
              <g key={connection.id}>
                <path
                  d={path}
                  fill="none"
                  stroke="#020617"
                  strokeWidth="1.8"
                  opacity="0.6"
                />
                {editing && (
                  <path
                    d={path}
                    fill="none"
                    stroke="transparent"
                    strokeWidth="4"
                    className="cursor-pointer"
                    onPointerDown={(event) => {
                      event.preventDefault();
                      onSelectConnection?.(connection);
                    }}
                  />
                )}
                <path
                  d={path}
                  fill="none"
                  stroke={guildDetails.color}
                  strokeWidth={selectedConnection ? "1.05" : "0.75"}
                  opacity={selectedConnection ? "1" : "0.85"}
                  className="path-dash"
                />
                {editing && (
                  <>
                    <polyline
                      points={`${from.x},${from.y} ${connection.controlX},${connection.controlY} ${to.x},${to.y}`}
                      fill="none"
                      stroke={guildDetails.color}
                      strokeWidth="0.25"
                      strokeDasharray="1 1"
                      opacity="0.7"
                    />
                    <circle
                      cx={connection.controlX}
                      cy={connection.controlY}
                      r={selectedConnection ? 1.35 : 1.05}
                      fill="#020617"
                      stroke={selectedConnection ? "#fbbf24" : guildDetails.color}
                      strokeWidth="0.35"
                      className="cursor-grab"
                      onPointerDown={(event) => {
                        event.preventDefault();
                        onSelectConnection?.(connection);
                        setDragTarget({ type: "connection", id: connection.id });
                      }}
                    />
                  </>
                )}
              </g>
            );
          })}
        </svg>

        {curriculum.regions.map((region) => {
          const unlocked = editing || isRegionUnlocked(region, progress);
          const completion = progress.regionCompletion[region.id] ?? 0;
          const selected = region.id === selectedRegionId;
          const availableQuests = curriculum.quests.filter(
            (quest) =>
              quest.regionId === region.id && quest.templateId !== "coming-soon",
          ).length;
          return (
            <button
              key={region.id}
              type="button"
              disabled={!unlocked}
              onClick={() => onSelect(region)}
              onPointerDown={(event) => {
                if (!editing || !onMoveRegion) return;
                event.preventDefault();
                onSelect(region);
                setDragTarget({ type: "region", id: region.id });
              }}
              className={clsx(
                "group absolute z-10 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center transition duration-300",
                editing && "cursor-grab touch-none",
                unlocked && "hover:z-20 hover:scale-110",
                !unlocked && "cursor-not-allowed opacity-55",
              )}
              style={{ left: `${region.x}%`, top: `${region.y}%` }}
              aria-label={`${region.name}, ${completion}% complete${unlocked ? "" : ", locked"}`}
            >
              <span
                className={clsx(
                  "relative grid h-14 w-14 place-items-center rounded-2xl border-4 bg-slate-950 shadow-xl transition",
                  selected && "scale-110 ring-4 ring-amber-300/25",
                )}
                style={{
                  borderColor: selected ? "#fbbf24" : unlocked ? region.color : "#334155",
                }}
              >
                <Icon
                  name={unlocked ? region.icon : "LockKeyhole"}
                  size={22}
                  style={{ color: unlocked ? region.color : "#64748b" }}
                />
                {unlocked && (
                  <span className="absolute -right-3 -top-3 min-w-9 rounded-full border-2 border-game-ink bg-emerald-400 px-1.5 py-1 font-retro text-[7px] text-slate-950">
                    {completion}%
                  </span>
                )}
              </span>
              <span className="mt-2 rounded-xl border border-slate-700/70 bg-slate-950/90 px-3 py-1.5 text-center shadow-lg backdrop-blur">
                <span className="block whitespace-nowrap text-[11px] font-extrabold text-white">
                  {region.order}. {region.name}
                </span>
                <span className="mt-0.5 block font-retro text-[6px] uppercase tracking-wider text-slate-500">
                  {availableQuests} live · {region.subAreas.length} areas
                </span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
